import React, { useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import { Stripe } from "@stripe/stripe-js";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useStripe, Elements } from "@stripe/react-stripe-js";
import {
  ModalProps,
  PanelHeader,
  Button,
  Icons,
  Modal,
  TextController,
  EmailController,
} from "@pushpress/shared-components";
import { useToggle } from "../../../hooks/state";
import { AppContext } from "../../../context/app";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { PaymentMethod, postSavePaymentMethod } from "../../../apis/billingApi";
import styles from "./CadPaymentModal.module.scss";

interface CadPaymentModalProps extends ModalProps {
  stripe: Stripe | PromiseLike<Stripe | null>;
  stripeClientSecret: string;
  userUuid: string;
  stripeCustomer: string;
  onCreate?: (p: PaymentMethod) => void;
}

interface CadForm {
  name: string;
  email: string;
}

const CadPaymentModal = (props: CadPaymentModalProps) => {
  const { visible, stripeClientSecret, stripe } = props;

  return (
    <Modal visible={visible}>
      <Elements
        stripe={stripe}
        options={{
          clientSecret: stripeClientSecret,
        }}
      >
        <CadPaymentModalContent {...props} />
      </Elements>
    </Modal>
  );
};

const CadPaymentModalContent = (props: CadPaymentModalProps) => {
  const { userUuid, stripeCustomer, stripeClientSecret, onCreate, closeModal } =
    props;

  const { client } = useContext(AppContext);
  const stripe = useStripe();
  const [cardSaving, savingCardActions] = useToggle(false);

  const { control, handleSubmit } = useForm<CadForm>({
    mode: "onTouched",
  });

  const { mutateAsync: savePaymentMethod } = useMutation(postSavePaymentMethod);

  const saveCard = async (values: CadForm) => {
    try {
      savingCardActions.on();
      const stripeMethods = await stripe;
      if (!stripeMethods) {
        throw new Error("Stripe not found");
      }
      const { setupIntent, error } = await stripeMethods.confirmAcssDebitSetup(
        stripeClientSecret,
        {
          payment_method: {
            billing_details: values,
          },
        },
      );
      if (error) {
        throw new Error(extractError(error));
      }
      if (!setupIntent) {
        throw new Error("Save payment error");
      }
      const stripePaymentMethodId = setupIntent?.payment_method! as string;
      const paymentMethod = await savePaymentMethod({
        userUuid: userUuid,
        clientUuid: client!.uuid,
        paymentMethodId: stripePaymentMethodId,
        customerId: stripeCustomer,
      });
      if (!paymentMethod) {
        throw new Error("Save payment is empty");
      }
      onCreate?.(paymentMethod.billing);
      closeModal?.();
    } catch (e: any) {
      toast.error(extractError(e), defaultToastOptions);
    } finally {
      savingCardActions.off();
    }
  };

  return (
    <div className={styles.container}>
      <PanelHeader title="Add Bank">
        <PanelHeader.ActionMenu>
          <Button
            buttonType="secondary"
            text="Cancel"
            size="medium"
            onClick={closeModal}
          />
          <Button
            buttonType="primary"
            text="Save"
            icon={Icons.Check}
            iconLocation="left"
            size="medium"
            loading={cardSaving}
            disabled={!stripe}
            onClick={handleSubmit(saveCard)}
          />
        </PanelHeader.ActionMenu>
      </PanelHeader>
      <div className={styles.content}>
        <form noValidate onSubmit={handleSubmit(saveCard)}>
          <TextController
            label="Your full name"
            control={control}
            name="name"
            required
          />
          <EmailController
            label="Your email"
            control={control}
            name="email"
            required
          />
        </form>
      </div>
    </div>
  );
};

export default CadPaymentModal;
